import { useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Row, Col, Card, Input, Select, Space, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useArticleEditor } from '../hooks/useArticleEditor';
import { useHeaderSlot } from '../components/header-slot';
import { useIsMobile } from '../hooks/useIsMobile';
import EditHeaderTitle from '../components/EditHeaderTitle';
import Tiptap from '../components/editor/Tiptap';
import SeoPanel from '../components/SeoPanel';
import RelatedSection from '../components/RelatedSection';
import TagsCard from '../components/TagsCard';
import CoverCard from '../components/CoverCard';
import PublishSettingsCard from '../components/PublishSettingsCard';

export default function ArticleEditPage() {
  const { id = '' } = useParams();
  const { state, patch, save, autosave, loading, legacyHtml, publicUrl } = useArticleEditor(id);
  const slotEl = useHeaderSlot();
  const mobile = useIsMobile();
  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories });

  if (loading) return <Spin size="large" className="block py-24 text-center" />;

  return (
    <>
      {slotEl &&
        createPortal(
          <EditHeaderTitle
            mobile={mobile}
            title={state.title}
            onTitleChange={(title) => patch({ title })}
            autosave={autosave}
          />,
          slotEl,
        )}
      {/* 手機：header 只放唯讀標題，編輯改在內文上方 */}
      {mobile && (
        <Input.TextArea
          variant="borderless"
          autoSize
          placeholder="文章標題…"
          value={state.title}
          // 標題是單行：顯示時自動換行，但不允許存入換行（會流進 <title>、OG、LINE Today feed）
          onChange={(e) => patch({ title: e.target.value.replace(/\s*\n+\s*/g, ' ') })}
          onPressEnter={(e) => e.preventDefault()}
          className="!mb-3 !px-0 !text-xl !font-semibold !leading-snug"
        />
      )}
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Tiptap
            key={id}
            initialJson={state.bodyJson}
            initialHtml={legacyHtml}
            onChange={(json) => patch({ bodyJson: json })}
            onImageInserted={autosave.flush}
          />
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" size="middle" className="w-full">
            <PublishSettingsCard
              id={id}
              state={state}
              save={save}
              autosave={autosave}
              publicUrl={publicUrl}
            />

            <CoverCard coverUrl={state.coverUrl} onChange={(v) => patch(v)} />

            <Card title="分類">
              <Select
                mode="multiple"
                className="w-full"
                placeholder="選擇分類"
                value={state.categoryIds}
                onChange={(v) => patch({ categoryIds: v })}
                options={(categories.data ?? []).map((ct) => ({ value: ct.id, label: ct.name }))}
              />
            </Card>

            <TagsCard value={state.tags} onChange={(tags) => patch({ tags })} />

            <SeoPanel
              value={state.seo}
              onChange={(seo) => patch({ seo })}
              fallbackTitle={state.title}
              bodyJson={state.bodyJson}
              excerpt={state.excerpt}
              coverUrl={state.coverUrl}
            />
          </Space>
        </Col>
      </Row>

      <div className="mt-4">
        <RelatedSection excludeId={id} value={state.relatedIds} onChange={(ids) => patch({ relatedIds: ids })} />
      </div>
    </>
  );
}
